import React, { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import AddIcon from '@mui/icons-material/Add';
import { setDialogOpen } from '../../store/Actions';
import { buildDetailPath, useAppNavigate } from '../../utils/Routing';
import '../entityView/EntityView.scss';

const CategoryCard = ({category, eventCount, onOpen, delay}) => {
  const accent = category.pinColor || '#10b981';
  return (
    <button
      type="button"
      className="entity-view__card"
      style={{'--card-delay': `${delay}ms`, '--card-accent': accent}}
      onClick={onOpen}
    >
      <div className="entity-view__category-row">
        <div
          className="entity-view__category-icon"
          style={{backgroundColor: accent}}
          aria-hidden="true"
        >
          {category.defaultEmoji}
        </div>
        <div className="entity-view__category-text">
          <div className="entity-view__card-name">{category.name}</div>
          <div className="entity-view__card-meta">
            <span className="entity-view__card-count-pill">
              {eventCount} {eventCount === 1 ? 'event' : 'events'}
            </span>
          </div>
        </div>
      </div>
    </button>
  );
};

export function CategoriesView() {
  const user = useSelector(state => state.loggedInUser);
  const categories = useSelector(state => state.viewingUserCategories) || {};
  const events = useSelector(state => state.viewingUserEvents) || {};
  const viewingUserAccount = useSelector(state => state.viewingUserAccount);
  const loggedInUserAccount = useSelector(state => state.loggedInUserAccount);
  const isOwnMap = viewingUserAccount && loggedInUserAccount
    && viewingUserAccount.id === loggedInUserAccount.id;
  const dispatch = useDispatch();
  const navigateTo = useAppNavigate();

  const categoryList = useMemo(() => Object.values(categories), [categories]);

  // Per-category event counts so we can sort by usage and surface a top stat.
  const eventCountByCategory = useMemo(() => {
    const counts = {};
    Object.values(events).forEach(event => {
      if(!event.categoryId) return;
      counts[event.categoryId] = (counts[event.categoryId] || 0) + 1;
    });
    return counts;
  }, [events]);

  const sortedCategories = useMemo(() => {
    return [...categoryList].sort((a, b) => {
      const diff = (eventCountByCategory[b.id] || 0) - (eventCountByCategory[a.id] || 0);
      if(diff !== 0) return diff;
      return (a.name || '').localeCompare(b.name || '');
    });
  }, [categoryList, eventCountByCategory]);

  const totalCategorizedEvents = sortedCategories.reduce(
    (sum, c) => sum + (eventCountByCategory[c.id] || 0),
    0
  );

  const ownerName = viewingUserAccount?.displayName || viewingUserAccount?.name || 'User';
  const heroTitle = isOwnMap ? 'My Categories' : `${ownerName}'s Categories`;
  const heroSubtitle = categoryList.length > 0
    ? `${categoryList.length} ${categoryList.length === 1 ? 'way' : 'ways'} to organize your memories`
    : 'Group your events by theme or place type';

  const username = viewingUserAccount?.username;
  const openCategory = (category) => {
    if(!username) return;
    navigateTo(buildDetailPath(username, 'Categories', category.id));
  };

  const openAddCategoryDialog = () => {
    dispatch(setDialogOpen('Edit Category', {
      parentDialog: '',
      dialogTitle: 'Add Category',
      category: {
        userId: user?.uid,
        name: '',
        defaultEmoji: '🏠',
        pinColor: '#FF0000',
      },
    }));
  };

  return (
    <main className="entity-view">
      <section className="entity-view__hero">
        <div className="entity-view__hero-glow entity-view__hero-glow--categories" aria-hidden="true"/>
        <div className="entity-view__hero-content">
          <div className="entity-view__hero-icon" aria-hidden="true">🗂️</div>
          <h1 className="entity-view__hero-title">{heroTitle}</h1>
          <div className="entity-view__hero-tagline">Categories</div>
          <div className="entity-view__hero-subtitle">{heroSubtitle}</div>
          {isOwnMap &&
            <div className="entity-view__hero-actions">
              <button
                type="button"
                className="entity-view__hero-add-btn"
                onClick={openAddCategoryDialog}
              >
                <AddIcon/>
                <span>Add Category</span>
              </button>
            </div>
          }
        </div>
      </section>

      {categoryList.length > 0 &&
        <section className="entity-view__stats-strip" aria-label="Category stats">
          <div
            className="entity-view__stat"
            style={{'--stat-delay': '120ms', '--stat-accent': '#10b981'}}
          >
            <div className="entity-view__stat-value">{categoryList.length}</div>
            <div className="entity-view__stat-label">Categories</div>
          </div>
          <div
            className="entity-view__stat"
            style={{'--stat-delay': '200ms', '--stat-accent': '#0ea5e9'}}
          >
            <div className="entity-view__stat-value">{totalCategorizedEvents}</div>
            <div className="entity-view__stat-label">Categorized Events</div>
          </div>
          {sortedCategories[0] &&
            <div
              className="entity-view__stat"
              style={{'--stat-delay': '280ms', '--stat-accent': '#6366f1'}}
            >
              <div className="entity-view__stat-value" style={{fontSize:18, lineHeight:1.2}}>
                {sortedCategories[0].defaultEmoji} {sortedCategories[0].name}
              </div>
              <div className="entity-view__stat-label">Most Used</div>
            </div>
          }
        </section>
      }

      {categoryList.length > 0 &&
        <section className="entity-view__panel">
          <header className="entity-view__panel-header">
            <h2>All Categories</h2>
            <span className="entity-view__panel-header-count">
              {categoryList.length} total
            </span>
          </header>
          <div className="entity-view__grid">
            {sortedCategories.map((category, idx) => (
              <CategoryCard
                key={category.id}
                category={category}
                eventCount={eventCountByCategory[category.id] || 0}
                onOpen={() => openCategory(category)}
                delay={120 + idx * 50}
              />
            ))}
          </div>
        </section>
      }

      {categoryList.length === 0 &&
        <section className="entity-view__empty">
          <div className="entity-view__empty-icon" aria-hidden="true">🗂️</div>
          <div className="entity-view__empty-title">No categories yet</div>
          <div className="entity-view__empty-text">
            Categories give every event a color and an emoji on the map — start by adding your first one.
          </div>
          {isOwnMap &&
            <button
              type="button"
              className="entity-view__hero-add-btn"
              onClick={openAddCategoryDialog}
            >
              <AddIcon/>
              <span>Add your first category</span>
            </button>
          }
        </section>
      }
    </main>
  );
}
